import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import WorkersPage from "@/pages/WorkersPage";
import ShiftTypesPage from "@/pages/ShiftTypesPage";
import StatisticsPage from "@/pages/StatisticsPage";
import WeeklyViewPage from "@/pages/WeeklyViewPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import InventoryPage from "@/pages/InventoryPage";
import ManagerPage from "@/pages/ManagerPage";
import BioimpedancePage from "@/pages/BioimpedancePage";
import SocialMediaPage from "@/pages/SocialMediaPage";
import CrmPage from "@/pages/CrmPage";
import AiChatPage from "@/pages/AiChatPage";
import TrainingPlanPage from "@/pages/TrainingPlanPage";
import NutritionPlanPage from "@/pages/NutritionPlanPage";
import AnamnesePage from "@/pages/AnamnesePage";
import RevenueDashboardPage from "@/pages/RevenueDashboardPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import ProfilePage from "@/pages/ProfilePage";
import AuthCallback from "@/components/AuthCallback";
import { ConnectionError } from "@/components/ConnectionError";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { LanguageProvider } from "@/contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [subscription, setSubscription] = useState(null);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setSubscription(null);
    setShowLogin(false);
  }, []);

  const loadSubscription = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/subscription/status`, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 });
      setSubscription(res.data);
      localStorage.setItem('is_admin', res.data.is_admin ? 'true' : 'false');
    } catch (err) {
      if (err.response?.status === 401) return;
      console.error("Subscription status failed:", err);
      setSubscription({ status: "error", message: err.response ? `HTTP ${err.response.status}` : err.message });
    }
  }, []);

  const retrySubscription = useCallback(() => {
    setSubscription(null);
    loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
    setIsLoading(false);
    if (token) loadSubscription();

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          toast.error("Session expired");
          handleLogout();
        }
        return Promise.reject(error);
      }
    );
    return () => { axios.interceptors.response.eject(interceptor); };
  }, [handleLogout, loadSubscription]);

  const handleLogin = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setIsAuthenticated(true);
    setShowLogin(false);
    setTimeout(loadSubscription, 500);
  };

  const hasAccess = (feature) => {
    if (!subscription || subscription.status === "none" || subscription.status === "expired" || subscription.status === "canceled" || subscription.status === "past_due") return false;
    return subscription.features?.includes(feature);
  };

  if (isLoading) return <div className="flex items-center justify-center h-screen">...</div>;

  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter
            isAuthenticated={isAuthenticated}
            showLogin={showLogin}
            setShowLogin={setShowLogin}
            handleLogin={handleLogin}
            handleLogout={handleLogout}
            subscription={subscription}
            loadSubscription={loadSubscription}
            retrySubscription={retrySubscription}
            hasAccess={hasAccess}
          />
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </LanguageProvider>
  );
}

function AppRouter({ isAuthenticated, showLogin, setShowLogin, handleLogin, handleLogout, subscription, loadSubscription, retrySubscription, hasAccess }) {
  const location = useLocation();

  // CRITICAL: Detect session_id synchronously during render, before ProtectedRoute runs
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onLogin={handleLogin} />;
  }

  const ProtectedRoute = ({ children, feature }) => {
    if (!isAuthenticated) return <Navigate to="/landing" />;
    if (subscription === null) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>;
    if (subscription.status === "error") return <ConnectionError message={subscription.message} onRetry={retrySubscription} onLogout={handleLogout} />;
    if (feature && !hasAccess(feature)) {
      return <Navigate to="/subscription" />;
    }
    return children;
  };

  const AdminRoute = ({ children }) => {
    if (!isAuthenticated) return <Navigate to="/landing" />;
    if (subscription === null) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>;
    if (subscription.status === "error") return <ConnectionError message={subscription.message} onRetry={retrySubscription} onLogout={handleLogout} />;
    if (!subscription?.is_admin) return <Navigate to="/" />;
    return children;
  };

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/landing" element={
        !isAuthenticated
          ? (showLogin ? <LoginPage onLogin={handleLogin} onBack={() => setShowLogin(false)} /> : <LandingPage onShowLogin={() => setShowLogin(true)} />)
          : <Navigate to="/" />
      } />
      <Route path="/login" element={
        !isAuthenticated
          ? <LoginPage onLogin={handleLogin} onBack={() => window.location.href = '/landing'} />
          : <Navigate to="/" />
      } />

      {/* Subscription management */}
      <Route path="/subscription" element={isAuthenticated ? <SubscriptionPage onLogout={handleLogout} subscription={subscription} onSubscriptionChange={loadSubscription} /> : <Navigate to="/landing" />} />
      <Route path="/subscription/success" element={isAuthenticated ? <SubscriptionPage onLogout={handleLogout} subscription={subscription} onSubscriptionChange={loadSubscription} /> : <Navigate to="/landing" />} />
      <Route path="/subscription/cancel" element={isAuthenticated ? <SubscriptionPage onLogout={handleLogout} subscription={subscription} onSubscriptionChange={loadSubscription} /> : <Navigate to="/landing" />} />
      <Route path="/profile" element={isAuthenticated ? <ProfilePage onLogout={handleLogout} /> : <Navigate to="/landing" />} />

      {/* Basic plan features */}
      <Route path="/" element={<ProtectedRoute feature="calendar"><DashboardPage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/manager" element={<ProtectedRoute feature="dashboard"><ManagerPage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/weekly" element={<ProtectedRoute feature="weekly"><WeeklyViewPage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute feature="inventory"><InventoryPage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/workers" element={<ProtectedRoute feature="workers"><WorkersPage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/shift-types" element={<ProtectedRoute feature="shift-types"><ShiftTypesPage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/training-plans" element={<ProtectedRoute feature="training-plans"><TrainingPlanPage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/nutrition" element={<ProtectedRoute feature="nutrition"><NutritionPlanPage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/anamnese" element={<ProtectedRoute feature="nutrition"><AnamnesePage onLogout={handleLogout} /></ProtectedRoute>} />

      {/* Premium plan features */}
      <Route path="/bioimpedance" element={<ProtectedRoute feature="bioimpedance"><BioimpedancePage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/social-media" element={<ProtectedRoute feature="social-media"><SocialMediaPage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/crm" element={<ProtectedRoute feature="crm"><CrmPage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/ai-chat" element={<ProtectedRoute feature="ai-chat"><AiChatPage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/statistics" element={<ProtectedRoute feature="statistics"><StatisticsPage onLogout={handleLogout} /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute feature="integrations"><IntegrationsPage onLogout={handleLogout} /></ProtectedRoute>} />

      {/* Admin only */}
      <Route path="/revenue" element={<AdminRoute><RevenueDashboardPage onLogout={handleLogout} /></AdminRoute>} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to={isAuthenticated ? "/subscription" : "/landing"} />} />
    </Routes>
  );
}

export default App;
